import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Building2, TrendingUp, TrendingDown, Coins, Calendar, Receipt, Users,
  ArrowUpDown, Search, Loader2, Sparkles,
} from "lucide-react";

// Range helpers — returns YYYY-MM-DD strings
function todayIso() { return new Date().toISOString().slice(0, 10); }
function addDaysIso(iso, delta) { const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + delta); return d.toISOString().slice(0, 10); }
function startOfWeekIso() { const d = new Date(); const dow = d.getUTCDay(); const shift = dow === 0 ? -6 : 1 - dow; d.setUTCDate(d.getUTCDate() + shift); return d.toISOString().slice(0, 10); }
function startOfMonthIso() { const d = new Date(); d.setUTCDate(1); return d.toISOString().slice(0, 10); }
function startOfYearIso() { const d = new Date(); d.setUTCMonth(0, 1); return d.toISOString().slice(0, 10); }

const PRESETS = [
  { k: "today", label: "Oggi" },
  { k: "week", label: "Settimana" },
  { k: "month", label: "Mese" },
  { k: "year", label: "Anno" },
  { k: "custom", label: "Personalizzato" },
];

const COLS = [
  { k: "name", label: "Salone", align: "left" },
  { k: "revenue", label: "Fatturato", align: "right", money: true },
  { k: "costs", label: "Costi", align: "right", money: true, color: "var(--sp-danger)" },
  { k: "margin", label: "Margine", align: "right", money: true },
  { k: "appointments_count", label: "Appuntamenti", align: "right" },
  { k: "invoices_count", label: "Fatture", align: "right" },
  { k: "avg_ticket", label: "Fiche media", align: "right", money: true },
];

export default function AgencyDashboard() {
  const [preset, setPreset] = useState("month");
  const [dateFrom, setDateFrom] = useState(startOfMonthIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("revenue");
  const [sortDir, setSortDir] = useState("desc");
  const [query, setQuery] = useState("");
  const [clientFilter, setClientFilter] = useState("all");   // all | clients | non
  const [clientSalonIds, setClientSalonIds] = useState(new Set());

  useEffect(() => {
    api.get("/admin/agency-clients")
      .then((r) => setClientSalonIds(new Set((r.data || []).map((c) => c.salon_tenant_id).filter(Boolean))))
      .catch(() => {});
  }, []);

  // Sync preset → date range
  useEffect(() => {
    const today = todayIso();
    if (preset === "today") { setDateFrom(today); setDateTo(today); }
    else if (preset === "week") { setDateFrom(startOfWeekIso()); setDateTo(today); }
    else if (preset === "month") { setDateFrom(startOfMonthIso()); setDateTo(today); }
    else if (preset === "year") { setDateFrom(startOfYearIso()); setDateTo(today); }
  }, [preset]);

  const load = () => {
    setLoading(true);
    api.get(`/admin/tenants?from=${dateFrom}&to=${dateTo}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  const tenants = data?.tenants || [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = tenants;
    if (q) list = list.filter((t) => (t.name || "").toLowerCase().includes(q));
    if (clientFilter === "clients") list = list.filter((t) => clientSalonIds.has(t.id));
    else if (clientFilter === "non") list = list.filter((t) => !clientSalonIds.has(t.id));
    list = [...list].sort((a, b) => {
      const av = a[sortBy] ?? 0; const bv = b[sortBy] ?? 0;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return list;
  }, [tenants, query, sortBy, sortDir, clientFilter, clientSalonIds]);

  const totals = useMemo(() => ({
    revenue: filtered.reduce((a, t) => a + (t.revenue || 0), 0),
    costs: filtered.reduce((a, t) => a + (t.costs || 0), 0),
    appts: filtered.reduce((a, t) => a + (t.appointments_count || 0), 0),
    invoices: filtered.reduce((a, t) => a + (t.invoices_count || 0), 0),
  }), [filtered]);
  const avgTicketAll = totals.invoices ? totals.revenue / totals.invoices : 0;

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("desc"); }
  };

  return (
    <div data-testid="agency-dashboard" className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Confronto Saloni</h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2">Situazione di ogni salone a colpo d'occhio nel periodo selezionato.</p>
        </div>
      </header>

      {/* Period picker */}
      <div className="sp-card p-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1" data-testid="period-preset">
          {PRESETS.map((p) => (
            <button key={p.k} onClick={() => setPreset(p.k)} data-testid={`period-${p.k}`}
              className={`px-3 py-1 rounded-full text-xs font-bold transition ${preset === p.k ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-xs font-bold text-[color:var(--sp-text-muted)]">Da</span>
            <input type="date" value={dateFrom} onChange={(e) => { setPreset("custom"); setDateFrom(e.target.value); }}
              className="sp-input !py-1 text-xs" data-testid="date-from" />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-xs font-bold text-[color:var(--sp-text-muted)]">a</span>
            <input type="date" value={dateTo} onChange={(e) => { setPreset("custom"); setDateTo(e.target.value); }}
              className="sp-input !py-1 text-xs" data-testid="date-to" />
          </label>
        </div>
        <div className="flex-1 min-w-[200px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
          <input placeholder="Cerca salone…" value={query} onChange={(e) => setQuery(e.target.value)}
            className="sp-input !pl-10 !py-1.5 text-sm w-full" data-testid="tenant-search" />
        </div>
        <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1" data-testid="client-filter">
          {[{ k: "all", label: "Tutti" }, { k: "clients", label: "Miei clienti" }, { k: "non", label: "Non clienti" }].map((f) => (
            <button key={f.k} onClick={() => setClientFilter(f.k)} data-testid={`client-filter-${f.k}`}
              className={`px-3 py-1 rounded-full text-xs font-bold transition ${clientFilter === f.k ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-[color:var(--sp-text-muted)]">
          {filtered.length} saloni · {loading && <Loader2 className="h-3 w-3 inline animate-spin" />}
        </div>
      </div>

      {/* Totals row */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={Building2} label="Saloni" value={filtered.length} />
        <Stat icon={TrendingUp} label="Fatturato" value={`€ ${totals.revenue.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`} />
        <Stat icon={TrendingDown} label="Costi" value={`€ ${totals.costs.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`} color="var(--sp-danger)" />
        <Stat icon={Calendar} label="Appuntamenti" value={totals.appts.toLocaleString("it-IT")} />
        <Stat icon={Receipt} label="Fiche media" value={`€ ${avgTicketAll.toLocaleString("it-IT", { maximumFractionDigits: 2 })}`} />
      </section>

      {/* Table */}
      <section className="sp-card overflow-hidden">
        {loading && !data ? (
          <div className="p-12 text-center text-[color:var(--sp-text-muted)]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-[color:var(--sp-text-secondary)]">Nessun salone corrisponde ai filtri.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--sp-subtle)] text-left">
              <tr className="label-eyebrow">
                <th className="p-3">#</th>
                {COLS.map((c) => (
                  <th key={c.k} className={`p-3 ${c.align === "right" ? "text-right" : ""} cursor-pointer hover:text-[color:var(--sp-brand)]`}
                      onClick={() => toggleSort(c.k)} data-testid={`sort-${c.k}`}>
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sortBy === c.k && <ArrowUpDown className="h-3 w-3" />}
                    </span>
                  </th>
                ))}
                <th className="p-3 text-right">Piano</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => (
                <tr key={t.id} data-testid={`tenant-row-${t.id}`} className={`${i % 2 ? "bg-[color:var(--sp-bg)]" : ""} hover:bg-[color:var(--sp-subtle)]/50 transition-colors`}>
                  <td className="p-3 text-xs font-black text-[color:var(--sp-text-muted)]">{i + 1}</td>
                  <td className="p-3">
                    <Link to={`/admin/tenants/${t.id}`} data-testid={`tenant-link-${t.id}`}
                      className="block hover:text-[color:var(--sp-brand)]">
                      <div className="font-bold flex items-center gap-2">
                        {t.name}
                        {t.suspended && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500 text-white uppercase tracking-widest">Sosp.</span>}
                      </div>
                      {t.address && <div className="text-xs text-[color:var(--sp-text-muted)] mt-0.5 truncate max-w-[240px]">{t.address}</div>}
                    </Link>
                  </td>
                  <td className="p-3 text-right font-display font-black text-[color:var(--sp-brand)]">€ {(t.revenue || 0).toLocaleString("it-IT")}</td>
                  <td className="p-3 text-right text-sm text-[color:var(--sp-danger)]">€ {(t.costs || 0).toLocaleString("it-IT")}</td>
                  <td className={`p-3 text-right font-bold text-sm ${(t.margin || 0) >= 0 ? "text-[color:var(--sp-success)]" : "text-[color:var(--sp-danger)]"}`}>€ {(t.margin || 0).toLocaleString("it-IT")}</td>
                  <td className="p-3 text-right text-sm">{(t.appointments_count || 0).toLocaleString("it-IT")}</td>
                  <td className="p-3 text-right text-sm">{(t.invoices_count || 0).toLocaleString("it-IT")}</td>
                  <td className="p-3 text-right text-sm font-bold">€ {(t.avg_ticket || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest font-bold ${
                      t.plan_tier === "premium" ? "bg-amber-100 text-amber-700"
                      : t.plan_tier === "pro" ? "bg-sky-100 text-sky-700"
                      : "bg-slate-100 text-slate-600"
                    }`}>
                      {t.plan_tier || "basic"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color = "var(--sp-brand)" }) {
  return (
    <div className="sp-card p-4 sp-anim">
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="font-display text-2xl font-black mt-2">{value}</div>
    </div>
  );
}
